/**
 * Pure logic for the WorkBar agents row (M4.2) and attention-bubble (M4.6)
 * subsystems.
 *
 * The component renders a roster of worker cards inside the `WorkBar` above
 * the composer. Each card shows status, branch, last message preview, and a
 * list of actions that depends on the worker's lifecycle state. The same
 * component is reused on a worker thread's own view with one row marked as
 * `isCurrent: true` (the "you" pill).
 *
 * This module owns the mappings so the React component stays a thin
 * projection over the view-model. All functions are pure and referentially
 * transparent so they can be unit-tested without mounting.
 */

import type { ThreadStatus, WorkerRole } from "@cmmd-forge/contracts";

/** Worker statuses that should surface an attention bubble. */
export const WORKER_STATUS_REQUIRES_ATTENTION: ReadonlySet<ThreadStatus> = new Set<ThreadStatus>([
  "stuck",
  "needs-review",
  "conflicted",
]);

/** Terminal (finished) worker statuses that sink below active work in the panel. */
const WORKER_STATUS_TERMINAL: ReadonlySet<ThreadStatus> = new Set<ThreadStatus>([
  "merged",
  "archived",
]);

/** Worker lifecycle statuses considered "active" for the `Team (N active)` header. */
const ACTIVE_STATUSES: ReadonlySet<ThreadStatus> = new Set<ThreadStatus>([
  "idle",
  "running",
  "stuck",
  "needs-review",
  "ready",
  "conflicted",
]);

export interface TeamPanelWorkerInput {
  readonly id: string;
  readonly title: string;
  readonly branch: string;
  readonly status: ThreadStatus;
  readonly lastMessage?: string;
  /** ms since epoch. Used for stable secondary sort (recent first within same attention tier). */
  readonly updatedAt?: number;
  readonly workerRole?: WorkerRole | null;
}

export interface WorkerCardViewModel {
  readonly id: string;
  readonly title: string;
  readonly branch: string;
  readonly status: ThreadStatus;
  readonly lastMessage?: string;
  readonly updatedAt?: number;
  readonly workerRole?: WorkerRole | null;
  /**
   * True when this row represents the thread currently being viewed. On worker
   * threads, `WorkBarAgentsRow` marks the matching row with a "you" pill and
   * drops its Open button.
   */
  readonly isCurrent?: boolean;
}

export interface TeamPanelViewModel {
  readonly workers: readonly WorkerCardViewModel[];
  readonly activeCount: number;
  readonly attentionCount: number;
  readonly runningCount: number;
}

export interface BuildTeamPanelOptions {
  /** When set, the matching worker row is marked `isCurrent: true`. */
  readonly currentWorkerId?: string;
}

export interface WorkerCardAction {
  readonly id: string;
  readonly label: string;
  readonly intent: "primary" | "secondary" | "destructive";
}

/**
 * Actions to render on a worker card, keyed by the worker's current status.
 * Selected to match the M4.2 spec — subtle differences vs protocol message
 * actions (those are per-message; these are per-worker card).
 */
export function pickWorkerActions(status: ThreadStatus): readonly WorkerCardAction[] {
  switch (status) {
    case "running":
      return [
        { id: "open", label: "Open", intent: "secondary" },
        { id: "cancel", label: "Cancel", intent: "destructive" },
      ];
    case "stuck":
    case "needs-review":
    case "ready":
      return [
        { id: "review", label: "Review", intent: "primary" },
        { id: "help", label: "Help", intent: "secondary" },
      ];
    case "conflicted":
      return [
        { id: "retry", label: "Retry", intent: "primary" },
        { id: "abandon", label: "Abandon", intent: "destructive" },
      ];
    case "merged":
      return [
        { id: "diff", label: "Diff", intent: "secondary" },
        { id: "archive", label: "Archive", intent: "secondary" },
      ];
    case "idle":
      return [{ id: "archive", label: "Archive", intent: "secondary" }];
    case "archived":
      return [];
    default: {
      const _exhaustive: never = status;
      void _exhaustive;
      return [];
    }
  }
}

/**
 * Count of workers with attention-requiring status. Used for the sidebar
 * row chip (`⚠N`) and the workspace row chip (sum across all masters).
 */
export function computeAttentionBubble(workers: readonly WorkerCardViewModel[]): number {
  let count = 0;
  for (const w of workers) {
    if (WORKER_STATUS_REQUIRES_ATTENTION.has(w.status)) count += 1;
  }
  return count;
}

/**
 * Build the view-model for the TeamPanel component. Sort order:
 *   1. Workers needing attention first (stuck/needs-review/conflicted).
 *   2. Within each tier, most recent `updatedAt` first.
 *   3. `merged` and `archived` sink to the bottom of the non-attention tier.
 */
export function buildTeamPanelViewModel(
  raw: readonly TeamPanelWorkerInput[],
  options: BuildTeamPanelOptions = {},
): TeamPanelViewModel {
  const { currentWorkerId } = options;

  const normalized: WorkerCardViewModel[] = raw.map((w) => {
    const card: WorkerCardViewModel = {
      id: w.id,
      title: w.title,
      branch: w.branch,
      status: w.status,
    };
    const withOptional = {
      ...card,
      ...(w.lastMessage !== undefined ? { lastMessage: w.lastMessage } : {}),
      ...(w.updatedAt !== undefined ? { updatedAt: w.updatedAt } : {}),
      ...(w.workerRole !== undefined ? { workerRole: w.workerRole } : {}),
      ...(currentWorkerId !== undefined && w.id === currentWorkerId ? { isCurrent: true } : {}),
    } as WorkerCardViewModel;
    return withOptional;
  });

  const sorted = [...normalized].toSorted((a, b) => {
    // Attention-required workers always come first.
    const aAttention = WORKER_STATUS_REQUIRES_ATTENTION.has(a.status) ? 1 : 0;
    const bAttention = WORKER_STATUS_REQUIRES_ATTENTION.has(b.status) ? 1 : 0;
    if (aAttention !== bAttention) return bAttention - aAttention;
    // Terminal workers (merged/archived) sink to the bottom of the non-attention tier.
    const aTerminal = WORKER_STATUS_TERMINAL.has(a.status) ? 1 : 0;
    const bTerminal = WORKER_STATUS_TERMINAL.has(b.status) ? 1 : 0;
    if (aTerminal !== bTerminal) return aTerminal - bTerminal;
    // Otherwise most-recent updatedAt wins.
    const aTime = a.updatedAt ?? 0;
    const bTime = b.updatedAt ?? 0;
    return bTime - aTime;
  });

  return {
    workers: sorted,
    activeCount: sorted.filter((w) => ACTIVE_STATUSES.has(w.status)).length,
    attentionCount: computeAttentionBubble(sorted),
    runningCount: sorted.filter((w) => w.status === "running").length,
  };
}

// ---------------------------------------------------------------------------
// Header label — master vs worker variants
// ---------------------------------------------------------------------------

export interface AgentsHeaderMasterLink {
  readonly threadId: string;
  readonly title: string;
}

export interface AgentsHeaderLabel {
  /** Primary label shown after the 🤖 icon. */
  readonly primary: string;
  /**
   * When present, the header renders a breadcrumb suffix: `· Part of <link>`.
   * Populated on worker threads so the user can jump back to the master.
   */
  readonly masterLink?: AgentsHeaderMasterLink;
}

export interface PickAgentsHeaderLabelInput {
  readonly isMaster: boolean;
  readonly isWorker: boolean;
  readonly workerCount: number;
  readonly masterLink?: AgentsHeaderMasterLink;
}

/**
 * Compute the header label for `WorkBarAgentsRow`.
 *
 * `workerCount` is the **total** number of workers on the team, including
 * the current thread when viewing a worker. That keeps the label stable
 * regardless of which thread is focused: a 3-worker team always reads
 * "Team (3)" from the master's view and "Workers (3)" from any worker's.
 *
 * Variants:
 * - master with N > 0 workers: `Team (N)`
 * - master with 0 workers: `Team`
 * - worker with N > 1 workers on team (self + siblings): `Workers (N)` + breadcrumb
 * - solo worker (N === 1): `Worker` + breadcrumb
 * - neither (plain thread): `Agents` (caller should generally hide the row)
 */
export function pickAgentsHeaderLabel(input: PickAgentsHeaderLabelInput): AgentsHeaderLabel {
  const { isMaster, isWorker, workerCount, masterLink } = input;
  if (isWorker) {
    const label: AgentsHeaderLabel = {
      primary: workerCount > 1 ? `Workers (${workerCount})` : "Worker",
      ...(masterLink !== undefined ? { masterLink } : {}),
    };
    return label;
  }
  if (isMaster) {
    return { primary: workerCount > 0 ? `Team (${workerCount})` : "Team" };
  }
  return { primary: "Agents" };
}
